// Live events from the API (wss://api2./v1/events): what happens on the user's
// phone line on any device, as seen by Asterisk. Reconnects by itself.

import type { Participant } from './api';
import { apiUrl } from './config';

export interface LiveCallEvent {
  type: 'call.incoming' | 'call.outgoing' | 'call.answered' | 'call.ended' | 'call.forwarded';
  callId: string;
  direction: 'inbound' | 'outbound';
  number: string;
  name?: string;
  at: string;
  result?: 'answered' | 'missed' | 'failed' | 'cancelled';
  reason?: string;
  durationSec?: number;
  /** call.incoming: which of the user's numbers the caller dialled (when known) */
  to?: string;
}

export type LiveEvent =
  | { type: 'hello'; userId: number }
  | { type: 'session.ended' }
  | { type: 'sip.changed' } // SIP password changed: fetch the softphone settings again
  | { type: 'voicemail.new'; id: number; callId: string; from: string }
  | { type: 'voicemail.changed' } // heard or deleted on another device
  | { type: 'readiness.changed' } // PR 1b: no number / credit line changed: reload GET /v1/me/readiness
  // PR 3b: how long a call just answered can last (plan minutes + credit): "about 1 minute left"
  | { type: 'call.limit'; callId: string; direction: 'inbound' | 'outbound'; number: string; endsAt: string; noticeSec: number }
  | { type: 'call.transfer'; callId: string; result: 'answered' | 'not_answered' } // blind transfer result
  | {
      // who is in a conference call now (sipCallId = the user's call that is the conference)
      type: 'call.conference';
      callId: string;
      sipCallId: string;
      participants: Participant[];
      version: number;
      ended?: true;
      note?: { number: string; result: 'not_answered' | 'busy'; transfer?: true };
    }
  | LiveCallEvent;

export class LiveEvents {
  private ws: WebSocket | null = null;
  private stopped = false;
  private retryMs = 1000;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly onEvent: (ev: LiveEvent) => void,
    private readonly onSessionEnded: () => void,
  ) {}

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.ws?.close(1000);
    this.ws = null;
  }

  private open(): void {
    const url = apiUrl.replace(/^http/, 'ws') + '/v1/events';
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onmessage = (e) => {
      let ev: LiveEvent;
      try {
        ev = JSON.parse(String(e.data)) as LiveEvent;
      } catch {
        return;
      }
      if (ev.type === 'hello') this.retryMs = 1000;
      if (ev.type === 'session.ended') {
        this.stop();
        this.onSessionEnded();
        return;
      }
      this.onEvent(ev);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (e.code === 4001) {
        this.stopped = true;
        this.onSessionEnded();
        return;
      }
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (this.stopped) return;
    const wait = this.retryMs;
    this.retryMs = Math.min(this.retryMs * 2, 30_000);
    this.retryTimer = setTimeout(() => this.open(), wait + Math.random() * 500);
  }
}
